import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ClassifyFields } from "./ClassifyFields";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const EditItemForm = ({ item, onCancel, onSaved }) => {
  const loc = item.location || {};
  const known = loc.country && loc.country !== "Unknown";
  const [date, setDate] = useState(item.date_unknown ? "" : (item.taken_at_local || "").slice(0, 16));
  const [city, setCity] = useState(known ? loc.city : "");
  const [region, setRegion] = useState(known ? loc.region || "" : "");
  const [country, setCountry] = useState(known ? loc.country : "");
  const [desc, setDesc] = useState(item.description || "");
  const [cls, setCls] = useState({ event: item.event || "", setting: item.setting || "", landmark: item.landmark || "" });
  const [suggest, setSuggest] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => api.get("/locations/suggest", { params: { q: city } }).then((r) => setSuggest(r.data.locations)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [city]);

  const pick = (s) => { setCity(s.city); setRegion(s.region || ""); setCountry(s.country); setSuggest([]); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const body = {};
    if (date && date.slice(0, 10) > today()) return setError("Date can't be in the future");
    if (date && date !== (item.taken_at_local || "").slice(0, 16)) body.taken_at_local = date;
    if (city || country) {
      if (!city.trim() || !country.trim()) return setError("City and country are both required");
      if (!known || city !== loc.city || country !== loc.country || region !== (loc.region || "")) body.location = { city, region: region || null, country };
    }
    if (desc.trim() && desc.trim() !== (item.description || "")) body.description = desc;
    if (!desc.trim() && item.description) return setError("Description can't be empty");
    for (const k of ["event", "setting", "landmark"]) {
      if ((cls[k] || "").trim() !== (item[k] || "")) body[k] = cls[k].trim();
    }
    if (!Object.keys(body).length) return onCancel();
    setBusy(true);
    try {
      onSaved((await api.patch(`/items/${item.id}`, body)).data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="info-panel-inner info-form" onSubmit={save} noValidate data-testid="lightbox-edit-form">
      <label className="info-label" htmlFor="edit-date">Date</label>
      <input id="edit-date" type="datetime-local" max={`${today()}T23:59`} value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-date-input" />
      <span className="info-label">Location</span>
      <input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="off" data-testid="edit-city-input" />
      {suggest.length > 0 && city && !(suggest.length === 1 && suggest[0].city === city) && (
        <ul className="info-suggest" data-testid="edit-location-suggestions">
          {suggest.map((s) => (
            <li key={`${s.city}-${s.region}-${s.country}`}>
              <button type="button" onClick={() => pick(s)} data-testid="edit-location-suggestion">{[s.city, s.region, s.country].filter(Boolean).join(", ")}</button>
            </li>
          ))}
        </ul>
      )}
      <input placeholder="Region / state (optional)" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="edit-region-input" />
      <input placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="edit-country-input" />
      <label className="info-label" htmlFor="edit-desc">Description</label>
      <textarea id="edit-desc" rows={4} maxLength={500} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="edit-description-input" />
      <ClassifyFields {...cls} onEvent={(v) => setCls((c) => ({ ...c, event: v }))} onSetting={(v) => setCls((c) => ({ ...c, setting: v }))}
        onLandmark={(v) => setCls((c) => ({ ...c, landmark: v }))} />
      {error && <p className="text-xs text-red-400" data-testid="edit-error">{error}</p>}
      <div className="flex gap-2 mt-1">
        <button className="info-save" disabled={busy} data-testid="edit-save-button">{busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}</button>
        <button type="button" className="info-cancel" onClick={onCancel} data-testid="edit-cancel-button">Cancel</button>
      </div>
    </form>
  );
};
